import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    Search,
    Plus,
    Filter,
    FileText,
    Activity,
    AlertCircle,
    ChevronLeft,
    ChevronRight,
    Calendar,
    FilePenLine,
    ChevronDown,
    Users
} from 'lucide-react';
import patientService from '../services/patientService';
import AppointmentForm from '../components/AppointmentForm';
import './PatientsExpanded.css';

const PatientsExpanded = ({ onCreateNew, onPatientSelect, onPatientEdit, onBilling }) => {
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('activo');
    const [showAppointmentModal, setShowAppointmentModal] = useState(false);
    const [selectedPatientId, setSelectedPatientId] = useState(null);
    const [perPage, setPerPage] = useState(50);
    const [user, setUser] = useState(null);
    const [showCenPatients, setShowCenPatients] = useState(true);
    const [cenPrefKey, setCenPrefKey] = useState('patients_show_gen_default');
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 50,
        total: 0
    });

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (userData) {
            try {
                const parsedUser = JSON.parse(userData);
                setUser(parsedUser);

                const userIdentifier = parsedUser?.id || parsedUser?.email || 'default';
                const prefKey = `patients_show_gen_${userIdentifier}`;
                setCenPrefKey(prefKey);

                const savedPreference = localStorage.getItem(prefKey);
                if (savedPreference !== null) {
                    setShowCenPatients(savedPreference === '1');
                }
            } catch (e) {
                console.error('Error parsing user data:', e);
            }
        }
    }, []);

    useEffect(() => {
        localStorage.setItem(cenPrefKey, showCenPatients ? '1' : '0');
    }, [cenPrefKey, showCenPatients]);

    useEffect(() => {
        fetchPatients();
    }, [pagination.current_page, searchTerm, statusFilter, perPage]);

    const getNormalizedRole = () => {
        if (!user) return '';

        let role = user.role;
        if (typeof role === 'object' && role !== null) {
            role = role.name || role.slug || '';
        }

        if (typeof role !== 'string') return '';
        return role.toLowerCase().trim();
    };

    // Check if user can view patient info (only admin and recepcion)
    const canViewPatientInfo = () => {
        const role = getNormalizedRole();
        if (!role) return false;
        // Debug role access
        if (role !== 'administrador' && role !== 'admin' && role !== 'recepcion' && role !== 'receptionist' && role !== 'reception' && role !== 'secretaria' && role !== 'superadmin' && role !== 'super admin') {
            // console.log('Role access denied for:', role);
        }
        return role === 'administrador' || role === 'admin' || role === 'recepcion' || role === 'receptionist' || role === 'reception' || role === 'secretaria' || role === 'superadmin' || role === 'super admin';
    };

    const normalizedRole = getNormalizedRole();
    const visiblePatients = showCenPatients
        ? patients
        : patients.filter((patient) => !/^(CEN|GEN)-\d+$/i.test(String(patient.patient_code || '').trim()));
    const hiddenCenCount = Math.max(0, patients.length - visiblePatients.length);

    const fetchPatients = async () => {
        try {
            setLoading(true);
            const data = await patientService.getAll({
                page: pagination.current_page,
                limit: perPage,
                search: searchTerm,
                status: statusFilter
            });

            if (data && data.data) {
                setPatients(Array.isArray(data.data) ? data.data : []);
                setPagination(prev => ({ ...prev, ...(data.pagination || {}), per_page: perPage }));
            } else if (Array.isArray(data)) {
                setPatients(data);
                setPagination(prev => ({ ...prev, total: data.length }));
            } else {
                console.warn('Formato de datos de pacientes inesperado:', data);
                setPatients([]);
            }
        } catch (err) {
            setError('Error al cargar pacientes. Por favor intente de nuevo.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setPagination(prev => ({ ...prev, current_page: 1 }));
    };

    const handleStatusChange = (e) => {
        setStatusFilter(e.target.value);
        setPagination(prev => ({ ...prev, current_page: 1 }));
    };

    const handlePerPageChange = (e) => {
        setPerPage(parseInt(e.target.value));
        setPagination(prev => ({ ...prev, current_page: 1 }));
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';

        // Extract YYYY-MM-DD part safely
        // Works for: "2023-10-25", "2023-10-25 12:00:00", "2023-10-25T00:00..."
        const datePart = String(dateString).substring(0, 10);

        // Manual formatting to avoid timezone shifts (T-1)
        // Regex expects YYYY-MM-DD
        const regex = /^(\d{4})-(\d{2})-(\d{2})/;
        const match = datePart.match(regex);

        if (match) {
            const [_, year, month, day] = match;
            return `${day}/${month}/${year}`;
        }

        // Fallback logic
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('es-MX', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Helper to generate initials from doctor name (mirroring backend logic)
    const getInitials = (name) => {
        if (!name || typeof name !== 'string') return 'PAC';

        let cleanName = name.trim();
        const titles = ['Dr.', 'Dra.', 'Doctor ', 'Doctora ', 'Lic.', 'Ing.'];

        titles.forEach(title => {
            // Case insensitive replace
            const regex = new RegExp(`^${title.replace('.', '\\.')}\\s*`, 'i');
            cleanName = cleanName.replace(regex, '');
        });

        cleanName = cleanName.trim();
        if (!cleanName) return 'PAC';

        const words = cleanName.split(/\s+/);
        let initials = '';

        for (const word of words) {
            if (word.length > 0) {
                initials += word.charAt(0).toUpperCase();
            }
        }

        return initials.substring(0, 4) || 'PAC';
    };

    return (
        <div className="patients-container">
            {/* Header */}
            <div className="patients-header">
                <div className="patients-title-wrapper">
                    <div className="patients-title">
                        <Users size={28} />
                        <h2>Pacientes</h2>
                    </div>
                    <p>Gestión completa de expedientes clínicos</p>
                </div>
                {!['medico', 'médico', 'doctor'].includes(normalizedRole) && (
                    <button onClick={onCreateNew} className="btn-new-patient">
                        <Plus size={20} />
                        <span>Nuevo Paciente</span>
                    </button>
                )}
            </div>



            {/* Results Bar */}
            <div className="results-bar">
                <div className="results-info">
                    {!loading && (
                        <span>
                            Se encontraron <strong>{visiblePatients.length}</strong> pacientes en esta página de un total de <strong>{pagination.total || patients.length}</strong>
                            {!showCenPatients && hiddenCenCount > 0 && (
                                <> (ocultando <strong>{hiddenCenCount}</strong> GEN)</>
                            )}
                        </span>
                    )}
                </div>
                <div className="results-controls">
                    <div className="per-page-control">
                        <span>Mostrar:</span>
                        <select value={perPage} onChange={handlePerPageChange} className="per-page-select">
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                    <div className="search-control">
                        <span>Buscar:</span>
                        <input
                            type="text"
                            placeholder="Nombre, teléfono..."
                            className="search-input-compact"
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>
                    <div className="cen-toggle-control">
                        <label className="cen-toggle-label">
                            <input
                                type="checkbox"
                                checked={showCenPatients}
                                onChange={(e) => setShowCenPatients(e.target.checked)}
                            />
                            <span>Mostrar expedientes GEN</span>
                        </label>
                    </div>
                    <div className="filter-control">
                        <select
                            className="status-select-compact"
                            value={statusFilter}
                            onChange={handleStatusChange}
                        >
                            <option value="activo">Activos</option>
                            <option value="inactivo">Inactivos</option>
                            <option value="">Todos</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Data Table */}
            <div className="patients-table-wrapper">
                {loading ? (
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Cargando pacientes...</p>
                    </div>
                ) : error ? (
                    <div className="error-state">
                        <AlertCircle className="mx-auto mb-4" size={48} />
                        <p>{error}</p>
                    </div>
                ) : visiblePatients.length === 0 ? (
                    <div className="empty-state">
                        <FileText size={48} />
                        <h3>No se encontraron pacientes</h3>
                        <p>Intenta ajustar los filtros o activar la opción de mostrar expedientes GEN.</p>
                    </div>
                ) : (
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th><ChevronDown size={12} /> Apellido Paterno</th>
                                <th><ChevronDown size={12} /> Apellido Materno</th>
                                <th><ChevronDown size={12} /> Nombre</th>
                                <th><ChevronDown size={12} /> Fecha Nacimiento</th>
                                <th><ChevronDown size={12} /> Expediente</th>
                                <th><ChevronDown size={12} /> Email</th>
                                <th><ChevronDown size={12} /> Celular</th>
                                <th><ChevronDown size={12} /> Última Consulta</th>
                                <th><ChevronDown size={12} /> Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visiblePatients.map((patient, index) => (
                                <tr key={patient.id} className={index % 2 === 0 ? 'row-even' : 'row-odd'}>
                                    <td>{patient.last_name || '-'}</td>
                                    <td>{patient.maternal_last_name || '-'}</td>
                                    <td>{patient.first_name || '-'}</td>
                                    <td>{formatDate(patient.date_of_birth)}</td>
                                    <td>
                                        <span className="expediente-badge">
                                            {patient.patient_code || `${getInitials(patient.preferred_doctor_name || patient.preferred_doctor)}-${patient.id}`}
                                        </span>
                                    </td>
                                    <td>{patient.email || '-'}</td>
                                    <td>{patient.phone || '-'}</td>
                                    <td>{formatDate(patient.last_visit) || '-'}</td>
                                    <td>
                                        <div className="action-buttons">
                                            {canViewPatientInfo() && (
                                                <button
                                                    onClick={() => onPatientEdit && onPatientEdit(patient.id)}
                                                    className="btn-action btn-info"
                                                    title="Ver/Editar Información"
                                                >
                                                    <FilePenLine size={16} />
                                                </button>
                                            )}
                                            {/* Appointment button removed as per user request */}
                                            {!['recepcion', 'recepción', 'receptionist', 'reception'].includes(normalizedRole) && (
                                                <button
                                                    onClick={() => onPatientSelect && onPatientSelect(patient.id)}
                                                    className="btn-action btn-expediente"
                                                    title="Ver Expediente"
                                                >
                                                    <FileText size={16} />
                                                </button>
                                            )}

                                            {/* Botón de cobrar oculto por solicitud */}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Pagination */}
            {!loading && patients.length > 0 && (
                <div className="pagination-bar">
                    <div className="pagination-info">
                        Mostrando {((pagination.current_page - 1) * perPage) + 1} a {Math.min(pagination.current_page * perPage, pagination.total || patients.length)} de {pagination.total || patients.length} pacientes
                    </div>
                    <div className="pagination-controls">
                        <button
                            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
                            disabled={pagination.current_page === 1}
                            className="btn-page"
                        >
                            <ChevronLeft size={18} />
                            Anterior
                        </button>
                        <span className="page-number">Página {pagination.current_page}</span>
                        <button
                            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
                            disabled={pagination.current_page * perPage >= (pagination.total || patients.length)}
                            className="btn-page"
                        >
                            Siguiente
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </div>
            )}

            {showAppointmentModal && selectedPatientId && (
                <AppointmentForm
                    patientId={selectedPatientId}
                    patientName={patients.find(p => p.id === selectedPatientId)?.first_name + ' ' + patients.find(p => p.id === selectedPatientId)?.last_name + (patients.find(p => p.id === selectedPatientId)?.maternal_last_name ? ' ' + patients.find(p => p.id === selectedPatientId)?.maternal_last_name : '')}
                    onSuccess={() => {
                        setShowAppointmentModal(false);
                        setSelectedPatientId(null);
                    }}
                    onCancel={() => {
                        setShowAppointmentModal(false);
                        setSelectedPatientId(null);
                    }}
                />
            )}
        </div>
    );
};

export default PatientsExpanded;
